import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import API_CONFIG from '../../config/api';
import { rotuloEscopoEditorDocumento } from '../../pages/contratos/utils/editorDocumentoEscopo';
import {
  HUD_DIALOG_PAPER,
  HUD_DIALOG_TITLE,
  HUD_DIALOG_CONTENT,
  HUD_CTA_BUTTON,
  HUD_CARD,
} from './hudDialogStyles';

const BACKEND_URL = API_CONFIG.BACKEND_URL;
const STORAGE_KEY_HISTORICO_MOLDRAS = 'contratos_historico_molduras_notificacao';

function carregarHistoricoMoldurasStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_HISTORICO_MOLDRAS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function salvarHistoricoMoldurasStorage(itens) {
  try {
    localStorage.setItem(STORAGE_KEY_HISTORICO_MOLDRAS, JSON.stringify(itens));
  } catch (e) {
    console.warn('Erro ao salvar histórico de molduras:', e);
  }
}

export default function EditorNotificacaoModal({
  open,
  onClose,
  processoId = null,
  fluxoId = null,
  escopo = null,
}) {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [molduraFile, setMolduraFile] = useState(null);
  const [molduraPreview, setMolduraPreview] = useState(null);
  const [historicoMolduras, setHistoricoMolduras] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const molduraInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      carregarMoldura();
      setHistoricoMolduras(carregarHistoricoMoldurasStorage());
    }
  }, [open]);

  const carregarMoldura = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/contratos/templates/notificacao`);
      if (response.ok) {
        const data = await response.json();
        if (data.moldura_url) {
          setMolduraPreview(data.moldura_url);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar moldura de Notificação:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMolduraUpload = async () => {
    if (!molduraFile) {
      setSnackbar({ open: true, message: 'Selecione um arquivo para upload.', severity: 'warning' });
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('moldura', molduraFile);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contratos/templates/notificacao/molduras`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao fazer upload da moldura');
      }

      const data = await response.json();
      setMolduraPreview(data.moldura_url || `${BACKEND_URL}/${data.moldura_path}`);
      setMolduraFile(null);
      setSnackbar({ open: true, message: 'Moldura enviada com sucesso!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao fazer upload da moldura:', error);
      setSnackbar({ open: true, message: error.message || 'Erro ao fazer upload da moldura', severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: HUD_DIALOG_PAPER }}>
        <DialogTitle sx={{ ...HUD_DIALOG_TITLE, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>
              Editar Moldura de Notificação
            </Typography>
            <Typography variant="body2" sx={{ color: '#99f6e4', mt: 0.25 }}>
              Fundo institucional para documentos de notificação
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={HUD_DIALOG_CONTENT}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            {rotuloEscopoEditorDocumento({ escopo, processoId, fluxoId })}
          </Alert>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                Moldura da Instituição
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Faça upload de uma imagem (PNG/JPG) que será usada como fundo para os documentos de Notificação.
              </Typography>

              <input
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                ref={molduraInputRef}
                style={{ display: 'none' }}
                onChange={(e) => setMolduraFile(e.target.files[0])}
              />

              <Box
                sx={{
                  ...HUD_CARD,
                  mb: 3,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  alignItems: 'center',
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderColor: 'rgba(13,148,136,0.35)',
                  bgcolor: 'rgba(13,148,136,0.04)',
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => molduraInputRef.current?.click()}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Selecionar Imagem da Moldura
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={handleMolduraUpload}
                  disabled={!molduraFile || uploading}
                  sx={HUD_CTA_BUTTON}
                >
                  {uploading ? <CircularProgress size={24} color="inherit" /> : 'Upload Moldura'}
                </Button>
                {molduraFile && (
                  <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
                    Arquivo selecionado: {molduraFile.name}
                  </Typography>
                )}
              </Box>

              {molduraPreview && (
                <Box sx={{ ...HUD_CARD, mt: 1 }}>
                  <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                    Pré-visualização da Moldura Atual
                  </Typography>
                  <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                    <img
                      src={molduraPreview}
                      alt="Moldura Preview"
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        maxHeight: '400px',
                        borderRadius: 12,
                        boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
                      }}
                    />
                  </Box>
                </Box>
              )}

              {historicoMolduras.length > 0 && (
                <Box sx={{ ...HUD_CARD, mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight={800} color="text.secondary" gutterBottom>
                    Histórico de molduras já anexadas
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 200, overflow: 'auto' }}>
                    {historicoMolduras.map((item, idx) => (
                      <Box component="li" key={idx} sx={{ mb: 0.5 }}>
                        <Typography variant="body2">
                          {item.nome}
                          {' — '}
                          <Typography component="span" variant="caption" color="text.secondary">
                            {item.data ? new Date(item.data).toLocaleString('pt-BR') : ''}
                          </Typography>
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

